// frontend/src/modules/esign/api.ts
//
// Typed client for the e-sign module. Two surfaces:
//   - `esignApi`: authenticated tenant routes (/api/esign/documents/*),
//     using the shared `api()` wrapper — matches every other module client.
//   - `esignPublicApi`: unauthenticated signer-facing routes
//     (/api/public/esign/*), gated by a per-recipient bearer token embedded
//     in the URL rather than a JWT. Plain `fetch` (mirrors marketing/api.ts's
//     getJson pattern) — never routes through `api()`, which would attach a
//     stale tenant-user JWT that these routes don't check.

import { api, getToken } from '../../core/api-client';
import type {
  EsignDocument,
  EsignRelatedType,
  DocumentDetail,
  DocumentsPage,
  ListDocumentsParams,
  RecipientInput,
  FieldInput,
  EsignSigningOrder,
  EsignSession,
  SignSubmission,
  VerifyResult,
  EsignTemplate,
  TemplateDetail,
  TemplatesPage,
  ListTemplatesParams,
  SaveTemplateBody,
  TemplateAssignmentInput,
} from './types';

function listQuery(params: ListDocumentsParams): string {
  const qs = new URLSearchParams();
  if (params.status) qs.set('status', params.status);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const tail = qs.toString();
  return tail ? `?${tail}` : '';
}

export const esignApi = {
  listDocuments: (params: ListDocumentsParams = {}) =>
    api<DocumentsPage>(`/api/esign/documents${listQuery(params)}`),

  getDocument: (id: string) =>
    api<DocumentDetail>(`/api/esign/documents/${id}`),

  /** Multipart upload — hand-rolled fetch, mirrors expenses' uploadAttachment. */
  createDocument: async (form: FormData): Promise<EsignDocument> => {
    const token = getToken();
    const res = await fetch('/api/esign/documents', {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    const text = await res.text();
    let json: any = null;
    if (text) { try { json = JSON.parse(text); } catch { /* leave null */ } }
    if (!res.ok) {
      const msg = json?.error?.message ?? `Upload failed (${res.status})`;
      throw new Error(msg);
    }
    return json.document as EsignDocument;
  },

  saveRecipients: (id: string, body: { recipients: RecipientInput[]; signing_order?: EsignSigningOrder }) =>
    api<{ recipients: DocumentDetail['recipients'] }>(`/api/esign/documents/${id}/recipients`, {
      method: 'PUT', body,
    }),

  saveFields: (id: string, body: { fields: FieldInput[] }) =>
    api<{ fields: DocumentDetail['fields'] }>(`/api/esign/documents/${id}/fields`, {
      method: 'PUT', body,
    }),

  send: (id: string, body: { reminders_enabled?: boolean; expires_in_days?: 7 | 14 | 30 } = {}) =>
    api<{ self_sign_url?: string }>(`/api/esign/documents/${id}/send`, { method: 'POST', body }),

  extend: (id: string) =>
    api<{ document: EsignDocument }>(`/api/esign/documents/${id}/extend`, { method: 'POST', body: {} }),

  /**
   * Rotation-free eligibility probe — safe to call passively (e.g. on
   * mount/refresh) to decide whether to show the "Sign now" button. Throws
   * `ApiError` (status 404, code `not_a_pending_signer`) when the caller
   * isn't currently an eligible-right-now signer on this document —
   * expected/silent for most viewers, so callers should catch and check
   * `.status` rather than surfacing the error.
   */
  checkMySignEligibility: (id: string) =>
    api<{ eligible: true }>(`/api/esign/documents/${id}/my-sign-link`),

  /**
   * Mutating — rotates the matched recipient's token and returns the
   * one-time sign URL. Only call this from an explicit user action (the
   * "Sign now" click), never from a passive probe/poll — use
   * `checkMySignEligibility` for that.
   */
  mySignLink: (id: string) =>
    api<{ url: string }>(`/api/esign/documents/${id}/my-sign-link`, { method: 'POST', body: {} }),

  /**
   * Attach the envelope to a business record — today only `employee` — or
   * detach it with `related_type: null`. Allowed in any status on purpose: the
   * common case is filing an already-signed agreement against the employee it
   * was signed for. The read side lives on the employee record
   * (hrApi.listEmployeeEsignDocuments).
   */
  setRelated: (id: string, body: { related_type: EsignRelatedType | null; related_id: string | null }) =>
    api<{ document: EsignDocument }>(`/api/esign/documents/${id}/related`, { method: 'PUT', body }),

  voidDoc: (id: string, reason?: string) =>
    api<void>(`/api/esign/documents/${id}/void`, { method: 'POST', body: { reason } }),

  resend: (id: string, rid: string) =>
    api<void>(`/api/esign/documents/${id}/recipients/${rid}/resend`, { method: 'POST', body: {} }),

  /**
   * Build the authenticated download URL. `original: true` asks for the
   * pre-signature source PDF — the only way to get bytes out of a completed
   * document whose sealing failed, and it is named "(unsigned original)" so
   * nobody mistakes it for the executed copy.
   */
  downloadUrl: (id: string, opts: { original?: boolean } = {}) =>
    `/api/esign/documents/${id}/download${opts.original ? '?original=1' : ''}`,

  /** Fetch the PDF as an authed blob and return an object URL for an <iframe>/<a>. */
  downloadPdf: async (id: string, opts: { original?: boolean } = {}): Promise<string> => {
    const token = getToken();
    const res = await fetch(esignApi.downloadUrl(id, opts), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      // The server explains WHY (e.g. seal_incomplete names the seal error).
      // Swallowing that into a bare status code is how a failed seal stayed
      // invisible in the first place.
      const detail = await res.json().then(
        (b: any) => b?.error?.message as string | undefined,
        () => undefined,
      );
      throw new Error(detail ?? `Download failed (${res.status})`);
    }
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },
};

function templateListQuery(params: ListTemplatesParams): string {
  const qs = new URLSearchParams();
  if (params.includeArchived) qs.set('include_archived', '1');
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const tail = qs.toString();
  return tail ? `?${tail}` : '';
}

// Reusable templates: save-as-template from a document, edit an existing
// template's roles/fields, archive, and instantiate (template + concrete
// per-role assignments -> a normal v1 draft document). There is NO
// create-from-scratch-upload route on the backend (Task 2 didn't add one —
// see templates.service.ts's `missing_original_blob_url` guard), so this
// client intentionally has no bare "create template" call; every template
// is born via `fromDocument`.
export const esignTemplatesApi = {
  listTemplates: (params: ListTemplatesParams = {}) =>
    api<TemplatesPage>(`/api/esign/templates${templateListQuery(params)}`),

  getTemplate: (id: string) =>
    api<TemplateDetail>(`/api/esign/templates/${id}`),

  updateTemplate: (id: string, body: SaveTemplateBody) =>
    api<{ template: EsignTemplate }>(`/api/esign/templates/${id}`, { method: 'PUT', body }),

  archiveTemplate: (id: string) =>
    api<void>(`/api/esign/templates/${id}/archive`, { method: 'POST', body: {} }),

  fromDocument: (documentId: string, name: string) =>
    api<{ template: EsignTemplate }>(`/api/esign/templates/from-document`, {
      method: 'POST', body: { document_id: documentId, name },
    }),

  instantiate: (id: string, body: { title: string; assignments: TemplateAssignmentInput[] }) =>
    api<{ document: EsignDocument }>(`/api/esign/templates/${id}/instantiate`, { method: 'POST', body }),

  /** Build the authenticated download URL for a template's source PDF (used with downloadPdf below). */
  downloadUrl: (id: string) => `/api/esign/templates/${id}/pdf`,

  /** Fetch a template's PDF as an authed blob and return an object URL — mirrors esignApi.downloadPdf. */
  downloadPdf: async (id: string): Promise<string> => {
    const token = getToken();
    const res = await fetch(esignTemplatesApi.downloadUrl(id), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },
};

// ---- Public (signer-facing, no JWT) ----

async function publicJson<T>(path: string, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(path, {
    method: opts.method ?? 'GET',
    headers: opts.body !== undefined ? { 'Content-Type': 'application/json' } : {},
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  const text = await res.text();
  let json: any = null;
  if (text) { try { json = JSON.parse(text); } catch { /* leave null */ } }
  if (!res.ok) {
    const msg = json?.error?.message ?? `Request failed (${res.status})`;
    const err = new Error(msg) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return json as T;
}

export const esignPublicApi = {
  getSession: (token: string) =>
    publicJson<EsignSession>(`/api/public/esign/session/${encodeURIComponent(token)}`),

  getSessionPdf: async (token: string): Promise<ArrayBuffer> => {
    const res = await fetch(`/api/public/esign/session/${encodeURIComponent(token)}/pdf`);
    if (!res.ok) throw new Error(`Failed to load the document (${res.status})`);
    return res.arrayBuffer();
  },

  sign: (token: string, body: SignSubmission) =>
    publicJson<{ status: 'signed' }>(`/api/public/esign/session/${encodeURIComponent(token)}/sign`, {
      method: 'POST', body,
    }),

  decline: (token: string, reason?: string) =>
    publicJson<{ status: 'declined' }>(`/api/public/esign/session/${encodeURIComponent(token)}/decline`, {
      method: 'POST', body: { reason },
    }),

  verify: (number: string) =>
    publicJson<VerifyResult>(`/api/public/esign/verify/${encodeURIComponent(number)}`),
};
